import React, { useMemo } from 'react';

const FREE_SHIPPING_TARGET = 8;

const FreeShippingProgress = ({ currentCount = 0 }) => {
  const progressPercentage = Math.min((currentCount / FREE_SHIPPING_TARGET) * 100, 100);
  const isFreeShippingUnlocked = currentCount >= FREE_SHIPPING_TARGET;

  // CHANGED: generate milestone positions for completed items (1, 2, ..., currentCount-1)
  const completedMilestones = useMemo(() => {
    const milestones = [];
    const maxCount = Math.min(currentCount, FREE_SHIPPING_TARGET);
    for (let i = 1; i < maxCount; i++) {
      milestones.push(i);
    }
    return milestones;
  }, [currentCount]);

  // CHANGED: use alternating gacha capsule images instead of 📦 emoji
  const milestoneIcons = useMemo(
    () =>
      completedMilestones.map((milestone) => {
        const milestonePercentage = (milestone / FREE_SHIPPING_TARGET) * 100;
        const isEven = milestone % 2 === 0;
        const src = isEven ? '/assets/blue_gacha.png' : '/assets/yellow_gacha.png';
        const alt = isEven ? 'Blue gachapon capsule' : 'Yellow gachapon capsule';
        return {
          id: milestone,
          left: `calc(${milestonePercentage}% - 0.75rem)`,
          src,
          alt,
        };
      }),
    [completedMilestones],
  );

  const currentIcon = useMemo(() => {
    if (progressPercentage <= 0) return null;
    if (isFreeShippingUnlocked) {
      return {
        src: '/assets/blue_gacha.png',
        alt: 'Free shipping unlocked',
        transform: 'translateY(-50%)',
      };
    }
    const nextMilestoneIndex = Math.max(1, Math.min(FREE_SHIPPING_TARGET, currentCount));
    const isEven = nextMilestoneIndex % 2 === 0;
    return {
      src: isEven ? '/assets/blue_gacha.png' : '/assets/yellow_gacha.png',
      alt: 'Gachapon progress',
      transform: 'translateY(-50%)',
    };
  }, [progressPercentage, isFreeShippingUnlocked, currentCount]);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative w-full py-2">
        <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-ponpon-navy/10">
          {/* CHANGED: progress bar color from yellow (gold) to coral brand color */}
          <div
            className="h-full rounded-full bg-ponpon-coral transition-all duration-500 ease-out"
            style={{ width: `${progressPercentage}%` }}
            role="progressbar"
            aria-valuenow={currentCount}
            aria-valuemin={0}
            aria-valuemax={FREE_SHIPPING_TARGET}
            aria-label={`Free shipping progress: ${currentCount} of ${FREE_SHIPPING_TARGET} items`}
          />
        </div>
        {/* CHANGED: render alternating yellow/blue gachapon icons at each completed milestone */}
        {milestoneIcons.map((icon) => (
          <span
            key={icon.id}
            className="absolute top-1/2 -translate-y-1/2 transition-all duration-500 ease-out"
            style={{ left: icon.left }}
            aria-hidden="true"
          >
            <img
              src={icon.src}
              alt={icon.alt}
              className="h-6 w-6 object-contain drop-shadow-[0_3px_6px_rgba(15,23,42,0.45)]"
              loading="lazy"
              decoding="async"
            />
          </span>
        ))}
        {/* CHANGED: current position gachapon icon at the front */}
        {currentIcon ? (
          <span
            className="absolute top-1/2 -translate-y-1/2 transition-all duration-500 ease-out"
            style={{ left: `calc(${progressPercentage}% - 0.9rem)`, transform: currentIcon.transform }}
            aria-hidden="true"
          >
            <img
              src={currentIcon.src}
              alt={currentIcon.alt}
              className="h-7 w-7 object-contain drop-shadow-[0_4px_8px_rgba(15,23,42,0.5)]"
              loading="lazy"
              decoding="async"
            />
          </span>
        ) : null}
      </div>
      <div className="flex items-center justify-end text-xs">
        <span className={`font-semibold ${isFreeShippingUnlocked ? 'text-ponpon-coral' : 'text-ponpon-navy/60'}`}>
          {isFreeShippingUnlocked ? 'Free shipping unlocked!' : `${FREE_SHIPPING_TARGET - currentCount} more to go`}
        </span>
      </div>
    </div>
  );
};

export default FreeShippingProgress;

